import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowLeft, BsPaperclip, BsXLg } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import "../../Assets/css/tickets.css";

const MAX_FILES = 5;
const TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

// Raise a ticket to the admins (Phase 7.7): salary, leave, equipment, app problems... Only the admins see it.
const RaiseTicket = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState(null);
    const [form, setForm] = useState({ category: "", reason: "", subject: "", description: "" });
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const input = useRef(null);

    useEffect(() => {
        ticketApi.options().then((r) => {
            if (!r.ok) { toast.error(r.message); setCategories([]); return; }
            setCategories(r.data.categories || []);
        });
    }, []);

    const category = (categories || []).find((c) => c.name === form.category);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const pick = (event) => {
        const chosen = [...(event.target.files || [])];
        event.target.value = "";
        if (chosen.some((f) => !TYPES.includes(f.type) || f.size > 10 * 1024 * 1024)) { toast.error("Attach photos or PDFs, each under 10 MB"); return; }
        setFiles([...files, ...chosen].slice(0, MAX_FILES));
    };
    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);
        const r = await ticketApi.raise({ category: form.category, reason: form.reason, subject: form.subject.trim(), description: form.description.trim() }, files);
        setBusy(false);
        if (!r.ok) { toast.error(r.message); return; }
        toast.success("Ticket raised. The admins have been told.");
        navigate(`/tickets/${r.data.ticket.ticketId}`, { replace: true });
    };
    const ready = form.category && (!category?.reasons?.length || form.reason) && form.subject.trim().length >= 3 && form.description.trim();

    return (
        <div className="tk-page" style={{ maxWidth: 720 }}>
            <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate("/tickets?queue=staff")}><BsArrowLeft aria-hidden="true" /> My tickets</button>
            <h4 className="fw-semibold mb-1">Raise a ticket</h4>
            <p className="text-muted">For anything you need the office to sort out. Only you and the admins see it.</p>
            {!categories ? <Skeleton height={300} /> : (
                <form className="tk-card" onSubmit={submit}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label small" htmlFor="rt-cat">Category</label>
                            <select id="rt-cat" className="form-select" value={form.category} onChange={(e) => set({ category: e.target.value, reason: "" })} required>
                                <option value="">Choose...</option>
                                {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small" htmlFor="rt-reason">Reason</label>
                            <select id="rt-reason" className="form-select" value={form.reason} disabled={!category?.reasons?.length} onChange={(e) => set({ reason: e.target.value })} required={!!category?.reasons?.length}>
                                <option value="">{category ? "Choose..." : "Pick a category first"}</option>
                                {(category?.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className="col-12">
                            <label className="form-label small" htmlFor="rt-subject">Subject</label>
                            <input id="rt-subject" className="form-control" maxLength={150} value={form.subject} onChange={(e) => set({ subject: e.target.value })} required />
                        </div>
                        <div className="col-12">
                            <label className="form-label small" htmlFor="rt-desc">What's the problem?</label>
                            <textarea id="rt-desc" className="form-control" rows={5} maxLength={2000} value={form.description} onChange={(e) => set({ description: e.target.value })} required />
                            <div className="form-text">{form.description.length}/2000</div>
                        </div>
                        <div className="col-12">
                            <input ref={input} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="d-none" onChange={pick} />
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => input.current?.click()} disabled={files.length >= MAX_FILES}><BsPaperclip aria-hidden="true" /> Attach photos or PDFs</button>
                            {files.length ? (
                                <ul className="tk-chosen">
                                    {files.map((f, i) => <li key={`${f.name}-${i}`}>{f.name} <button type="button" className="btn btn-link btn-sm p-0" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}><BsXLg aria-hidden="true" /></button></li>)}
                                </ul>
                            ) : null}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={busy || !ready}>{busy ? "Sending..." : "Raise ticket"}</button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default RaiseTicket;
